import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import ReactMarkdown from 'react-markdown';
import { BlogItemList } from '../../components/blog';
import { IBlogItemFeaturedProps } from '../../components/blog/BlogItemFeatured';
import TextblocksWithIcon, {
  ITextblocksWithIconProps,
} from '../../components/common/blocks/TextblocksWithIcon';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import TeamMemberHeading, {
  ITeamMemberHeadingProps,
} from '../../components/team-members/TeamMemberHeading';
import {
  ITeamMemberListProps,
  TeamMemberList,
} from '../../components/team-members/TeamMemberList';
import { useAppSelector } from '../../hooks';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IBlogPost,
  IBlogPostsResult,
  IContactMethod,
  ICustomerReviewsMeta,
  IFaqLandingPage,
  ILayoutPageProps,
  ILinkWithIconComponent,
  ITeamMemberPage,
  ITeamMemberSkillsComponent,
  ITextBlockWithIcon,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateTeamMemberProfileSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  flatStrapiObject,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getTranslation,
} from '../../shared/useUtils';
import { BlogStyle } from '../../styles/pages/blog';
import { NextPageWithLayout } from '../_app';

export interface ITeamMemberPageProps extends ILayoutPageProps {
  fullname: string;
  jobTitle: string;
  biography: string;
  socialMediaIcons: ILinkWithIconComponent[];
  avatarUrl: string;
  education: ITextBlockWithIcon;
  expertise: ITextBlockWithIcon;
  expertiseSkills: ITeamMemberSkillsComponent;
  publishedArticles: string;
  contactMethods: IContactMethod[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  latestPostsTitle: string;
  posts: IBlogPost[];
  otherMembersTitle: string;
  otherMembersSubTitle: string;
  others: {
    fullname: string;
    jobTitle: string;
    avatarUrl: string;
    slug: string;
  }[];
}

const TeamMemberPage: NextPageWithLayout<ITeamMemberPageProps> = (
  props: ITeamMemberPageProps
) => {
  const router = useRouter();
  const appPages = useAppSelector((state) => state.app.pages);

  const mapSocialMediaIcons = (icons: ILinkWithIconComponent[]) => {
    return icons.map((socialMediaIcon) => ({
      iconUrl:
        getImageSrcWithFallback(
          flatStrapiObject(socialMediaIcon.icon),
          'full'
        ) || '/assets/images/home/map-image.png',
      link: socialMediaIcon.link,
    }));
  };

  const teamMemberHeadingProps: ITeamMemberHeadingProps = {
    fullname: props.fullname,
    jobTitle: props.jobTitle,
    biography: props.biography,
    socialMediaIcons: mapSocialMediaIcons(props.socialMediaIcons || []),
    imageUrl: props.avatarUrl || '',
  };

  const educationAndExpertise: ITextblocksWithIconProps = {
    largeIcons: true,
    items: [
      {
        content: props.education.content,
        icon: flatStrapiObject(props.education.icon),
      },
      {
        content: props.expertise.content,
        icon: flatStrapiObject(props.expertise.icon),
      },
    ],
  };

  const latestPosts: IBlogItemFeaturedProps[] = props.posts.map(
    (post: IBlogPost) => ({
      ...post,
      appPages: appPages || [],
    })
  );

  const teamMemberListProps: ITeamMemberListProps = {
    title: props.otherMembersTitle,
    subTitle: props.otherMembersSubTitle,
    teamMembers: props.others,
  };

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link
          rel="canonical"
          href={getCanonicalLink(router.locale || 'en', props.meta.slug)}
        />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <TeamMemberHeading {...teamMemberHeadingProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <TextblocksWithIcon {...educationAndExpertise} />
        </div>
      </div>

      {props.publishedArticles && props.publishedArticles !== 'n/a' && (
        <div className="page-content-section is-no-bottom-padding">
          <div className="page-content-block">
            <ReactMarkdown>{props.publishedArticles}</ReactMarkdown>
          </div>
        </div>
      )}

      <div className="page-content-section">
        <div className="page-content-block">
          <h2>{props.latestPostsTitle}</h2>
          <div className={BlogStyle.blog}>
            <div className={BlogStyle.listPosts}>
              {latestPosts.map((data: any, index: number) => {
                return <BlogItemList key={index} {...data} />;
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <TeamMemberList {...teamMemberListProps} />
        </div>
      </div>
    </div>
  );
};

TeamMemberPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticPaths(context: any) {
  let paths: any[] = [];

  const urlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const pages: ITeamMemberPage[] = flatStrapiCollection(
    await fetchStrapi<any>(`/team-members`, urlParams, buildApiKey)
  );

  for (const locale of context.locales) {
    const localizedPaths = pages.map((item) => {
      const localizedMeta = getTranslation(item.meta, locale);
      return {
        params: {
          slug: localizedMeta.slug,
        },
        locale: locale,
      };
    });
    paths = [...paths, ...localizedPaths];
  }

  return { paths, fallback: false };
}

export async function getStaticProps(
  context: any
): Promise<{ props: ITeamMemberPageProps }> {
  const appRoutes = await getRoutes();
  const paramSlug = context.params.slug;

  const pageUrlParams = new URLSearchParams({
    'filters[meta][slug][$eq]': paramSlug,
    'populate[0]': 'meta',
    'populate[1]': 'avatar',
    'populate[2]': 'jobTitle',
    'populate[3]': 'socialMediaIcons',
    'populate[4]': 'socialMediaIcons.icon',
    'populate[5]': 'biography',
    'populate[6]': 'education',
    'populate[7]': 'education.icon',
    'populate[8]': 'expertise',
    'populate[9]': 'expertise.icon',
    'populate[10]': 'expertiseSkills',
    'populate[11]': 'expertiseSkills.skills',
    'populate[12]': 'publishedArticles',
    'populate[13]': 'latestPostsTitle',
    'populate[14]': 'otherMembersTitle',
    'populate[15]': 'otherMembersSubTitle',
  });

  const pageResult = await fetchStrapi<any>(
    `/team-members`,
    pageUrlParams,
    buildApiKey
  );
  const page = flatStrapiCollection<ITeamMemberPage>(pageResult)[0];

  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);

  // Get other authors
  const pageOthersUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'avatar',
    'populate[2]': 'jobTitle',
  });

  const pageOthersResult = await fetchStrapi<any>(
    `/team-members`,
    pageOthersUrlParams,
    buildApiKey
  );
  const others = flatStrapiCollection<ITeamMemberPage>(pageOthersResult)
    .filter((item) => item.id !== page.id)
    .map((item) => ({
      fullname: item.fullname,
      jobTitle: getTranslation(item.jobTitle, context.locale).text as string,
      avatarUrl:
        getImageSrcWithFallback(flatStrapiObject(item.avatar), 'medium') ||
        '/assets/images/home/map-image.png',
      slug: getTranslation(item.meta, context.locale).slug,
    }));

  // Get related FAQs
  const faqsUrlParams = new URLSearchParams({
    locale: context.locale,
    'populate[0]': 'title',
    'populate[1]': 'body',
    'populate[2]': 'image',
    'populate[3]': 'image.image',
    'populate[4]': 'metas',
    'populate[5]': 'summary',
    'populate[6]': 'author',
    'fields[0]': 'updatedAt',
    'filters[author][meta][slug][$eq]': getTranslation(
      page.meta,
      context.locale
    ).slug,
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });
  const faqsResponse = await fetchStrapi<any>(
    `/faq-landings`,
    faqsUrlParams,
    buildApiKey
  );
  const faqs: IFaqLandingPage[] =
    flatStrapiCollection<IFaqLandingPage>(faqsResponse);
  const faqsMappedAsBlogPosts: IBlogPost[] = faqs.map((faq) => ({
    title: getTranslation(faq.title, context.locale).text as string,
    body: getTranslation(faq.body, context.locale).text as string,
    slug: getTranslation(faq.metas, context.locale).slug,
    image: faq.image
      ? flatStrapiObject(getTranslation(faq.image, context.locale)?.image)
      : undefined,
    metaDescription: getTranslation(faq.summary, context.locale).text as string,
    author: flatStrapiObject(faq.author),
    publicationDate: faq.updatedAt,
  }));

  // Get related blog posts
  const blogPostsUrlParams = new URLSearchParams({
    locale: context.locale,
    'fields[0]': 'id',
    'fields[1]': 'title',
    'fields[3]': 'publicationDate',
    'fields[4]': 'metaDescription',
    'fields[5]': 'slug',
    'filters[author][meta][slug][$eq]': getTranslation(
      page.meta,
      context.locale
    ).slug,
    'pagination[start]': '0',
    'pagination[limit]': '-1',
    disableLocalizations: 'true',
  });
  const blogPostsResponse: IBlogPostsResult =
    await fetchStrapi<IBlogPostsResult>(
      `/blog-posts/published`,
      blogPostsUrlParams,
      buildApiKey
    );
  const blogPosts = blogPostsResponse.results;

  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const commonTranslations = await getCommonTranslations();
  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );

  const teamMemberSchema = generateTeamMemberProfileSchema(
    page.fullname,
    getTranslation(page.jobTitle, context.locale).text as string,
    getTranslation(page.biography, context.locale).text as string,
    (
      getTranslation(
        page.expertiseSkills,
        context.locale
      ) as ITeamMemberSkillsComponent
    ).skills.map((skill) => skill.name),
    page.socialMediaIcons.map((icon) => icon.link)
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema, ...teamMemberSchema],
  };

  const meta = getTranslation(page.meta, context.locale);
  const originalPageMeta = getTranslation(page.meta, 'en');
  const alternativeLinks = getAlternateLinks(
    `/team-members/${originalPageMeta.slug}`,
    appRoutes
  );
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);

  const props: ITeamMemberPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    fullname: page.fullname,
    jobTitle: getTranslation(page.jobTitle, context.locale).text as string,
    biography: getTranslation(page.biography, context.locale).text as string,
    socialMediaIcons: page.socialMediaIcons,
    education: getTranslation(
      page.education,
      context.locale
    ) as ITextBlockWithIcon,
    expertise: getTranslation(
      page.expertise,
      context.locale
    ) as ITextBlockWithIcon,
    expertiseSkills: getTranslation(
      page.expertiseSkills,
      context.locale
    ) as ITeamMemberSkillsComponent,
    publishedArticles: getTranslation(page.publishedArticles, context.locale)
      .text as string,
    avatarUrl:
      getImageSrcWithFallback(flatStrapiObject(page.avatar), 'full') ||
      '/assets/images/home/map-image.png',
    contactMethods,
    canonicalLink,
    alternativeLinks,
    latestPostsTitle: getTranslation(page.latestPostsTitle, context.locale)
      .text as string,
    posts: [...blogPosts, ...faqsMappedAsBlogPosts].sort((a, b) => {
      const dateA = a.publicationDate
        ? new Date(a.publicationDate).getTime()
        : 0;
      const dateB = b.publicationDate
        ? new Date(b.publicationDate).getTime()
        : 0;
      return dateB - dateA; // Descendente: más reciente primero
    }),
    otherMembersTitle: getTranslation(page.otherMembersTitle, context.locale)
      .text as string,
    otherMembersSubTitle: getTranslation(
      page.otherMembersSubTitle,
      context.locale
    ).text as string,
    others,
  };

  return {
    props,
  };
}

export default TeamMemberPage;
